import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Grid,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  createTrainingSessionTemplate,
  getTrainingSessionTemplates,
  updateTrainingSessionTemplate,
  deleteTrainingSessionTemplate,
  createStandardTrainingSessionTemplate,
  getSessionTemplateCatalog,
} from "../../components/funtionsBD/Mainservice";
import { gettype, getsubtype } from "../../components/funtionsBD/Mainservice";
import { ip_back } from "../../config";
import AdminLayout from "../../components/AdminLayout";
import DurationMinutesSecondsInput from "../../components/DurationMinutesSecondsInput";
import {
  parseDurationMinutes,
  formatDurationLabel,
  formatMinutesForStorage,
} from "../../utils/trainingDuration";
import CatalogExercisePicker, { catalogExerciseLabel } from "../../components/CatalogExercisePicker";
import { currentAppLanguage } from "../../utils/appLanguages";
import { localizedTypeName, localizedSubTypeName } from "../../utils/localizedCatalogFields";
import {
  isPersonalizedExerciseType,
  isWarmupExerciseType,
  isServiceExerciseType,
  bothPlayersPerformSubtype,
} from "../../utils/teExecutionType";
import "./TrainingSessionTemplates.css";

function parseTemplateMeta(template) {
  if (!template?.tst_meta) return null;
  if (typeof template.tst_meta === "object") return template.tst_meta;
  try {
    return JSON.parse(template.tst_meta);
  } catch {
    return null;
  }
}

function roleLabel(role, t) {
  if (role === "active") return t("performance_role", "Performance");
  if (role === "sparring") return t("sparring") || "Sparring";
  if (role === "both") return t("both") || "Both";
  return role;
}

function RoleChip({ role, who, t }) {
  const isPerf = role === "active" || role === "both";
  return (
    <span className={`st-role ${isPerf ? "st-role--perf" : "st-role--spar"}`}>
      {who}: {roleLabel(role, t)}
    </span>
  );
}

function LayoutHall({ stations }) {
  const n = Math.min(Math.max(stations?.length || 1, 1), 3);
  return (
    <div className={`st-hall st-hall--${n}`} aria-hidden="true">
      {(stations || []).slice(0, 3).map((s, i) => (
        <div key={i} className="st-lane" data-mode={s.timer_mode || "synced"}>
          {s.name}
        </div>
      ))}
    </div>
  );
}

export function TrainingSessionTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [catalog, setCatalog] = useState({ layouts: [], blocks: [], sessions: [] });
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("catalog");
  const [seedingKey, setSeedingKey] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [types, setTypes] = useState([]);
  const [subTypes, setSubTypes] = useState([]);
  const [subTypesByType, setSubTypesByType] = useState({}); // Store subtypes by type_id
  const [themes, setThemes] = useState([]);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentlng = currentAppLanguage(i18n);
  const info = JSON.parse(localStorage.getItem("user"));

  // Form state
  const [formData, setFormData] = useState({
    tst_name: "",
    tst_description: "",
    tst_duration: "",
    use_recommended_cb: false,
    coach_id: info?.coach_id || "",
    club_id: info?.club_id || "",
    items: [],
  });

  useEffect(() => {
    loadTemplates();
    loadCatalog();
    loadTypes();
    loadThemes();
  }, []);

  const loadCatalog = async () => {
    setCatalogLoading(true);
    try {
      const response = await getSessionTemplateCatalog({ lang: currentlng.slice(0, 2) });
      setCatalog(response.data.data || { layouts: [], blocks: [], sessions: [] });
    } catch (error) {
      console.error("Error loading catalog:", error);
    } finally {
      setCatalogLoading(false);
    }
  };

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const params = {};
      if (info?.coach_id) params.coach_id = info.coach_id;
      if (info?.club_id) params.club_id = info.club_id;
      const response = await getTrainingSessionTemplates(params);
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error("Error loading templates:", error);
      setSnackbar({
        open: true,
        message: t("errorLoadingTemplates", "Error loading templates"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTypes = async () => {
    try {
      const response = await gettype();
      setTypes(response.data.data || []);
    } catch (error) {
      console.error("Error loading types:", error);
    }
  };

  const loadSubTypes = async (typeId) => {
    if (!typeId) {
      return;
    }
    
    // Check if we already have subtypes for this type
    if (subTypesByType[typeId]) {
      return;
    }
    
    try {
      const response = await getsubtype(typeId);
      const subtypes = response.data.data || [];
      // Cache subtypes by type_id
      setSubTypesByType(prev => ({ ...prev, [typeId]: subtypes }));
    } catch (error) {
      console.error("Error loading subtypes:", error);
      // Still cache empty array to avoid repeated failed requests
      setSubTypesByType(prev => ({ ...prev, [typeId]: [] }));
    }
  };

  const loadThemes = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${ip_back}api/admin/getTheme`,
        {
          headers: {
            Authorization: token,
          },
        }
      );
      if (response.data.status === "success") {
        setThemes(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading themes:", error);
    }
  };

  const handleOpenDialog = async (template = null) => {
    if (template) {
      setEditingTemplate(template);
      const sortedItems = (template.template_items || [])
        .slice()
        .sort((a, b) => (a.tsti_order ?? 9999) - (b.tsti_order ?? 9999));
      const items = sortedItems.map((item) => {
        const rawSeqs = item.sequences || [];
        // Sort sequences by sequence_order, ensuring valid values are used
        const sortedSeqs = [...rawSeqs].sort((a, b) => {
          const orderA = a.sequence_order && a.sequence_order > 0 ? a.sequence_order : 9999;
          const orderB = b.sequence_order && b.sequence_order > 0 ? b.sequence_order : 9999;
          return orderA - orderB;
        });
        // Always assign sequence_order based on sorted array position (1-indexed)
        const sequences = sortedSeqs.map((seq, idx) => ({
          sub_type_id: seq.sub_type_id || "",
          player_a_role: seq.player_a_role || "active",
          player_b_role: seq.player_b_role || "sparring",
          sequence_duration: seq.sequence_duration || 0,
          sequence_order: idx + 1, // Always use array index + 1 as the source of truth
        }));
        return {
          type_id: item.type_id,
          tsti_duration: item.tsti_duration,
          use_recommended_cb: !!item.use_recommended_cb,
          tsti_slot_role: item.tsti_slot_role || "",
          defer_exercise: !!item.defer_exercise,
          ex_id: item.defer_exercise ? "" : item.ex_id || item.base_exercise?.ex_id || "",
          base_exercise: item.defer_exercise ? null : item.base_exercise || null,
          sequences,
        };
      }) || [];
      
      setFormData({
        tst_name: template.tst_name || "",
        tst_description: template.tst_description || "",
        tst_duration: template.tst_duration || "",
        use_recommended_cb: !!template.use_recommended_cb,
        coach_id: template.coach_id || info?.coach_id || "",
        club_id: template.club_id || info?.club_id || "",
        items: items,
      });
      
      // Load subtypes for all unique type_ids in the template
      const uniqueTypeIds = [...new Set(items.map(item => item.type_id).filter(Boolean))];
      for (const typeId of uniqueTypeIds) {
        await loadSubTypes(typeId);
      }
    } else {
      setEditingTemplate(null);
      setFormData({
        tst_name: "",
        tst_description: "",
        tst_duration: "",
        use_recommended_cb: false,
        coach_id: info?.coach_id || "",
        club_id: info?.club_id || "",
        items: [],
      });
      setSubTypes([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTemplate(null);
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          type_id: "",
          tsti_duration: "",
          use_recommended_cb: false,
          defer_exercise: false,
          ex_id: "",
          base_exercise: null,
          sequences: [],
        },
      ],
    });
  };

  const handleAddSequence = (itemIndex) => {
    const newItems = formData.items.map((it, i) => {
      if (i !== itemIndex) return it;
      const sequences = [...(it.sequences || [])];
      sequences.push({
        sub_type_id: "",
        player_a_role: "active",
        player_b_role: "sparring",
        sequence_duration: 0,
        sequence_order: sequences.length + 1,
      });
      const withOrder = sequences.map((s, idx) => ({ ...s, sequence_order: idx + 1 }));
      return { ...it, sequences: withOrder };
    });
    setFormData({ ...formData, items: newItems });
  };

  const calculateSequencesTotalDuration = (itemIndex) => {
    const item = formData.items[itemIndex];
    if (!item.sequences || item.sequences.length === 0) return 0;
    return item.sequences.reduce((sum, seq) => sum + parseDurationMinutes(seq.sequence_duration), 0);
  };

  const getRemainingSequenceTime = (itemIndex) => {
    const item = formData.items[itemIndex];
    const exerciseDuration = parseDurationMinutes(item.tsti_duration);
    const sequencesDuration = calculateSequencesTotalDuration(itemIndex);
    return exerciseDuration - sequencesDuration;
  };

  const handleRemoveSequence = (itemIndex, sequenceIndex) => {
    const newItems = formData.items.map((it, i) => {
      if (i !== itemIndex) return it;
      const sequences = (it.sequences || []).filter((_, j) => j !== sequenceIndex);
      const withOrder = sequences.map((s, idx) => ({ ...s, sequence_order: idx + 1 }));
      return { ...it, sequences: withOrder };
    });
    setFormData({ ...formData, items: newItems });
  };

  const handleMoveSequenceUp = (itemIndex, sequenceIndex) => {
    if (sequenceIndex === 0) return;
    const newItems = formData.items.map((it, i) => {
      if (i !== itemIndex) return it;
      const sequences = [...(it.sequences || [])];
      [sequences[sequenceIndex - 1], sequences[sequenceIndex]] = [sequences[sequenceIndex], sequences[sequenceIndex - 1]];
      const withOrder = sequences.map((s, idx) => ({ ...s, sequence_order: idx + 1 }));
      return { ...it, sequences: withOrder };
    });
    setFormData({ ...formData, items: newItems });
  };

  const handleMoveSequenceDown = (itemIndex, sequenceIndex) => {
    const newItems = formData.items.map((it, i) => {
      if (i !== itemIndex) return it;
      const sequences = [...(it.sequences || [])];
      if (sequenceIndex === sequences.length - 1) return it;
      [sequences[sequenceIndex], sequences[sequenceIndex + 1]] = [sequences[sequenceIndex + 1], sequences[sequenceIndex]];
      const withOrder = sequences.map((s, idx) => ({ ...s, sequence_order: idx + 1 }));
      return { ...it, sequences: withOrder };
    });
    setFormData({ ...formData, items: newItems });
  };

  const handleSequenceChange = (itemIndex, sequenceIndex, field, value) => {
    const newItems = formData.items.map((it, i) => {
      if (i !== itemIndex) return it;
      const sequences = (it.sequences || []).map((seq, j) => {
        if (j !== sequenceIndex) return seq;
        const next = { ...seq, [field]: value };
        if (field !== "sub_type_id") return next;
        const typeRow = types.find((row) => row.type_id === it.type_id);
        const pool = subTypesByType[it.type_id] || subTypes.filter((st) => st.type_id === it.type_id);
        const sub = pool.find((st) => st.sub_type_id === value);
        const subName = sub?.sub_type_name_en || sub?.[`sub_type_name_${currentlng}`] || "";
        if (bothPlayersPerformSubtype(typeRow, subName)) {
          next.player_a_role = "active";
          next.player_b_role = "active";
          return next;
        }
        const alternates =
          isWarmupExerciseType(typeRow) ||
          isServiceExerciseType(typeRow) ||
          String(typeRow?.type_name_en || "").toLowerCase().includes("competency");
        if (!alternates) return next;
        const prevSame = (it.sequences || [])
          .slice(0, sequenceIndex)
          .reverse()
          .find((row) => row.sub_type_id === value);
        const swap =
          prevSame?.player_a_role === "active" && prevSame?.player_b_role === "sparring";
        next.player_a_role = swap ? "sparring" : "active";
        next.player_b_role = swap ? "active" : "sparring";
        return next;
      });
      return { ...it, sequences };
    });
    setFormData({ ...formData, items: newItems });
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleMoveItemUp = (index) => {
    if (index === 0) return;
    const newItems = [...formData.items];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    setFormData({ ...formData, items: newItems });
  };

  const handleMoveItemDown = (index) => {
    if (index === formData.items.length - 1) return;
    const newItems = [...formData.items];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = async (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };

    
    if (field === "type_id") {
      await loadSubTypes(value);
      const typeRow = types.find((row) => row.type_id === value);
      if (isPersonalizedExerciseType(typeRow)) {
        newItems[index].ex_id = "";
        newItems[index].base_exercise = null;
        newItems[index].sequences = [];
        newItems[index].defer_exercise = false;
      }
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const handleBaseExerciseChange = async (index, exercise) => {
    const newItems = [...formData.items];
    const item = { ...newItems[index] };
    item.ex_id = exercise?.ex_id || "";
    item.base_exercise = exercise || null;
    if (exercise?.ex_id) item.defer_exercise = false;
    const nextTypeId = exercise?.type_id || exercise?.tbl_type_ex?.type_id;
    if (nextTypeId && nextTypeId !== item.type_id) {
      item.type_id = nextTypeId;
      await loadSubTypes(nextTypeId);
    }
    newItems[index] = item;
    setFormData({ ...formData, items: newItems });
  };

  const calculateRemainingTime = () => {
    const totalItemDuration = formData.items.reduce(
      (sum, item) => sum + parseDurationMinutes(item.tsti_duration),
      0
    );
    return (parseInt(formData.tst_duration) || 0) - totalItemDuration;
  };

  const handleSubmit = async () => {
    try {
      // Validate sequences duration doesn't exceed exercise duration
      for (let i = 0; i < formData.items.length; i++) {
        const item = formData.items[i];
        if (item.sequences && item.sequences.length > 0) {
          const totalSequencesDuration = calculateSequencesTotalDuration(i);
          if (totalSequencesDuration > parseDurationMinutes(item.tsti_duration)) {
            setSnackbar({
              open: true,
              message: t("sequencesDurationExceedsExercise") || `Total sequences duration (${totalSequencesDuration} min) exceeds exercise duration (${item.tsti_duration} min)`,
              severity: "error",
            });
            return;
          }
        }
      }

      // Validate required fields
      if (!formData.tst_name || !formData.tst_duration || formData.items.length === 0) {
        setSnackbar({
          open: true,
          message: t("templateRequiredFields", "Please fill all required fields"),
          severity: "error",
        });
        return;
      }

      // Validate club_id (coach_id will be retrieved automatically by backend if missing)
      if (!formData.club_id) {
        console.error("Missing club_id:", { club_id: formData.club_id, info });
        setSnackbar({
          open: true,
          message: t("templateRequiredFields", "Club information is missing. Please refresh the page and try again."),
          severity: "error",
        });
        return;
      }

      // Validate each item has required fields
      for (let i = 0; i < formData.items.length; i++) {
        const item = formData.items[i];
        if (!item.type_id || !item.tsti_duration) {
          setSnackbar({
            open: true,
            message: t("templateRequiredFields", "Each exercise must have a type and duration"),
            severity: "error",
          });
          return;
        }
        // Validate sequences if present (only require sub_type when subtypes exist and use_recommended_cb is off)
        if (item.sequences && item.sequences.length > 0 && !item.use_recommended_cb) {
          const subtypesForType = subTypesByType[item.type_id] || [];
          const hasSubtypes = subtypesForType.length > 0;
          for (let j = 0; j < item.sequences.length; j++) {
            const sequence = item.sequences[j];
            if (hasSubtypes && !sequence.sub_type_id) {
              setSnackbar({
                open: true,
                message: t("templateRequiredFields", "Each sequence must have a sub-type"),
                severity: "error",
              });
              return;
            }
          }
        }
      }

      const remainingTime = calculateRemainingTime();
      if (remainingTime < 0) {
        setSnackbar({
          open: true,
          message: t(
            "templateDurationExceeded",
            "Total items duration exceeds template duration"
          ),
          severity: "error",
        });
        return;
      }

      const payload = {
        ...formData,
        items: formData.items.map((item) => {
          const raw = item.sequences || [];
          // Sort by sequence_order, but ensure we use array index as fallback for consistent ordering
          const sorted = [...raw].sort((a, b) => {
            const orderA = a.sequence_order && a.sequence_order > 0 ? a.sequence_order : 9999;
            const orderB = b.sequence_order && b.sequence_order > 0 ? b.sequence_order : 9999;
            return orderA - orderB;
          });
          const { base_exercise: _baseExercise, ...rest } = item;
          return {
            ...rest,
            defer_exercise: !!rest.defer_exercise,
            ex_id: rest.defer_exercise ? null : rest.ex_id || null,
            // Explicitly set sequence_order based on sorted array position (1-indexed)
            sequences: sorted.map((seq, idx) => ({
              ...seq,
              sequence_order: idx + 1, // Always use array index + 1 as the source of truth
            })),
          };
        }),
      };
      if (editingTemplate) {
        await updateTrainingSessionTemplate(editingTemplate.tst_id, payload);
        setSnackbar({
          open: true,
          message: t("templateUpdated", "Template updated successfully"),
          severity: "success",
        });
      } else {
        await createTrainingSessionTemplate(payload);
        setSnackbar({
          open: true,
          message: t("templateCreated", "Template created successfully"),
          severity: "success",
        });
      }
      
      handleCloseDialog();
      loadTemplates();
    } catch (error) {
      console.error("Error saving template:", error);
      setSnackbar({
        open: true,
        message: error.message || t("errorSavingTemplate", "Error saving template"),
        severity: "error",
      });
    }
  };

  const handleDeleteClick = (e, template) => {
    e.preventDefault();
    e.stopPropagation();
    setTemplateToDelete(template);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;
    const tst_id = templateToDelete.tst_id;
    setDeleting(true);
    try {
      await deleteTrainingSessionTemplate(tst_id);
      setTemplateToDelete(null);
      setSnackbar({
        open: true,
        message: t("templateDeleted", "Template deleted successfully"),
        severity: "success",
      });
      loadTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      const message =
        (error?.data?.message || error?.message) ||
        t("errorDeletingTemplate", "Error deleting template");
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (!deleting) setTemplateToDelete(null);
  };
  const handleAddFromCatalog = async (modelKey) => {
    try {
      setSeedingKey(modelKey);
      const response = await createStandardTrainingSessionTemplate({
        club_id: info?.club_id || "",
        coach_id: info?.coach_id || "",
        model_key: modelKey,
        lang: currentlng.slice(0, 2),
      });
      const skipped = response?.data?.skipped;
      setSnackbar({
        open: true,
        message: skipped
          ? t("catalogTemplateExists", "Already in your club catalog")
          : t("standardTemplateCreated", "Added to your club templates"),
        severity: "success",
      });
      await loadTemplates();
      setActiveTab("club");
    } catch (error) {
      console.error("Error creating catalog template:", error);
      setSnackbar({
        open: true,
        message:
          error?.data?.message ||
          error?.message ||
          t("errorCreatingTemplate", "Error creating template"),
        severity: "error",
      });
    } finally {
      setSeedingKey(null);
    }
  };

  const remainingTime = calculateRemainingTime();
  const clubKeys = new Set(
    (templates || []).map((tpl) => tpl.tst_catalog_key).filter(Boolean)
  );

  const renderCatalogCard = (entry) => {
    const inClub = clubKeys.has(entry.key);
    const stations = entry.stations || [];
    return (
      <article key={entry.key} className="st-card">
        <div className="st-card__top">
          <h3 className="st-card__title">{entry.name}</h3>
          <span className={`st-pill st-pill--${entry.kind}`}>{entry.kind}</span>
        </div>
        <p className="st-card__desc">{entry.description}</p>
        {entry.kind === "layout" && stations.length > 0 ? (
          <LayoutHall stations={stations} />
        ) : null}
        {entry.kind === "block" && entry.sequences?.length > 0 ? (
          <ol className="st-seq">
            {entry.sequences.map((seq) => (
              <li key={`${entry.key}-${seq.order}`}>
                <span className="st-seq__n">{seq.order}</span>
                <span className="st-seq__name">
                  {seq.sub_type_name_en || t("recommendedCB", "Recommended CB")}{" "}
                  <span style={{ color: "var(--st-muted)", fontWeight: 500 }}>
                    ({formatDurationLabel(seq.sequence_duration) ||
                      `${seq.sequence_duration}${t("min") || "min"}`})
                  </span>
                </span>
                <span className="st-seq__roles">
                  <RoleChip who="A" role={seq.player_a_role} t={t} />
                  <RoleChip who="B" role={seq.player_b_role} t={t} />
                </span>
              </li>
            ))}
          </ol>
        ) : null}
        <div className="st-card__foot">
          <div className="st-meta">
            {entry.duration ? (
              <span>
                {entry.duration} {t("minutes") || "min"}
              </span>
            ) : null}
            {stations.length > 0 ? (
              <span>
                {stations.length} {t("stations", "stations")}
              </span>
            ) : null}
            {entry.sequences?.length ? (
              <span>
                {entry.sequences.length} {t("sequences") || "sequences"}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            className="st-btn st-btn--primary st-btn--sm"
            disabled={!!seedingKey || inClub}
            onClick={() => handleAddFromCatalog(entry.key)}
          >
            {seedingKey === entry.key
              ? t("creating") || "Adding…"
              : inClub
                ? t("inClubCatalog", "In club")
                : t("addToClub", "Add to club")}
          </button>
        </div>
      </article>
    );
  };

  return (
    <AdminLayout>
      <div className="st-board">
        <div className="st-board__glow" aria-hidden="true" />

        <header className="st-hero">
          <div className="st-hero__copy">
            <p className="st-hero__kicker">
              {t("sessionTemplatesKicker", "Pingflo · Session design")}
            </p>
            <h1 className="st-hero__title">
              {t("sessionTemplates") || "Session Templates"}
            </h1>
            <p className="st-hero__sub">
              {t(
                "sessionTemplatesLede",
                "Start from a room layout or a training-block recipe, then refine sequences and roles for your club."
              )}
            </p>
          </div>
          <div className="st-hero__actions">
            <button
              type="button"
              className="st-btn st-btn--primary"
              onClick={() => handleOpenDialog()}
            >
              {t("createTemplate") || "Create Template"}
            </button>
          </div>
        </header>

        <nav className="st-tabs" aria-label={t("sessionTemplateViews", "Views")}>
          <button
            type="button"
            className={activeTab === "catalog" ? "is-on" : ""}
            onClick={() => setActiveTab("catalog")}
          >
            {t("pingfloCatalog", "Pingflo catalog")}
          </button>
          <button
            type="button"
            className={activeTab === "club" ? "is-on" : ""}
            onClick={() => setActiveTab("club")}
          >
            {t("clubTemplates", "Club templates")} ({templates.length})
          </button>
        </nav>

        {(loading || catalogLoading) && (
          <div className="st-loading" aria-hidden="true">
            <span />
          </div>
        )}

        {activeTab === "catalog" && (
          <>
            <h2 className="st-section__label">
              {t("sessionLayoutsTitle", "Session layouts")}
            </h2>
            <p className="st-section__hint">
              {t(
                "sessionLayoutsHint",
                "How you split the hall: dual lanes, rotating stations, level bands, coach focus, or King of the Table."
              )}
            </p>
            <div className="st-grid">
              {(catalog.layouts || []).map(renderCatalogCard)}
            </div>

            <h2 className="st-section__label">
              {t("trainingBlockCatalogTitle", "Training blocks")}
            </h2>
            <p className="st-section__hint">
              {t(
                "trainingBlockCatalogHint",
                "Pre-shaped blocks with exercise characteristics and role sequences — pick matching catalogue drills when you plan."
              )}
            </p>
            <div className="st-grid">
              {(catalog.blocks || []).map(renderCatalogCard)}
            </div>

            <h2 className="st-section__label">
              {t("fullSessionModelsTitle", "Full session models")}
            </h2>
            <div className="st-grid">
              {(catalog.sessions || []).map(renderCatalogCard)}
            </div>
          </>
        )}

        {activeTab === "club" && (
          <>
            {!loading && templates.length === 0 ? (
              <div className="st-empty">
                {t(
                  "noTemplates",
                  "No templates in your club yet. Add one from the Pingflo catalog or create your own."
                )}
              </div>
            ) : (
              <div className="st-grid">
                {templates.map((template) => {
                  const meta = parseTemplateMeta(template);
                  const kind = template.tst_kind || "custom";
                  const items = (template.template_items || [])
                    .slice()
                    .sort((a, b) => (a.tsti_order ?? 9999) - (b.tsti_order ?? 9999));
                  return (
                    <article key={template.tst_id} className="st-card">
                      <div className="st-card__top">
                        <h3 className="st-card__title">{template.tst_name}</h3>
                        <span className={`st-pill st-pill--${kind === "custom" ? "session" : kind}`}>
                          {kind}
                        </span>
                      </div>
                      {template.tst_description ? (
                        <p className="st-card__desc">{template.tst_description}</p>
                      ) : null}
                      {kind === "layout" && meta?.stations ? (
                        <LayoutHall
                          stations={meta.stations.map((s) => ({
                            name:
                              typeof s.name === "string"
                                ? s.name
                                : s.name?.[currentlng.slice(0, 2)] ||
                                  s.name?.en ||
                                  s.role,
                            timer_mode: s.timer_mode,
                          }))}
                        />
                      ) : null}
                      <div className="st-meta">
                        <span>
                          {template.tst_duration} {t("minutes") || "min"}
                        </span>
                        <span>
                          {items.length} {t("trainingBlocks") || "blocks"}
                        </span>
                      </div>
                      {items.length > 0 ? (
                        <ol className="st-seq">
                          {items.slice(0, 6).map((item, idx) => {
                            const personalized = isPersonalizedExerciseType(
                              item.type_ex ||
                                types.find((row) => row.type_id === item.type_id)
                            );
                            const typeName = personalized
                              ? t("personalizedExercise", "Personalized exercise")
                              : localizedTypeName(item.type_ex, currentlng) || "—";
                            return (
                              <li key={item.tsti_id || idx}>
                                <span className="st-seq__n">{idx + 1}</span>
                                <span
                                  className={`st-seq__name${
                                    personalized ? " st-seq__name--personalized" : ""
                                  }`}
                                >
                                  {typeName}{" "}
                                  <span style={{ color: "var(--st-muted)", fontWeight: 500 }}>
                                    ({formatDurationLabel(item.tsti_duration) ||
                                      `${item.tsti_duration}${t("min") || "min"}`})
                                  </span>
                                  {!personalized && item.base_exercise ? (
                                    <span style={{ display: "block", fontWeight: 500, color: "var(--st-muted)" }}>
                                      {catalogExerciseLabel(item.base_exercise, currentlng)}
                                    </span>
                                  ) : null}
                                </span>
                                <span className="st-seq__roles">
                                  {(item.sequences || []).length
                                    ? `${item.sequences.length} ${t("sequences") || "seq."}`
                                    : null}
                                </span>
                              </li>
                            );
                          })}
                        </ol>
                      ) : null}
                      <div className="st-card__foot">
                        <div />
                        <div className="st-card__actions">
                          <button
                            type="button"
                            className="st-icon-btn"
                            aria-label={t("editTemplate") || "Edit template"}
                            onClick={() => handleOpenDialog(template)}
                          >
                            <EditIcon fontSize="small" />
                          </button>
                          <button
                            type="button"
                            className="st-icon-btn st-icon-btn--danger"
                            aria-label={t("deleteTemplate") || "Delete template"}
                            onClick={(e) => handleDeleteClick(e, template)}
                          >
                            <DeleteIcon fontSize="small" />
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </>
        )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 3 },
        }}
      >
        <DialogTitle sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", gap: 1, pb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {editingTemplate ? t("editTemplate") || "Edit Template" : t("createTemplate") || "Create Template"}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" color={remainingTime >= 0 ? "text.secondary" : "error.main"} sx={{ fontWeight: 500, textAlign: isMobile ? "center" : "right" }}>
            {t("totalRemaining") || "Total Remaining"}: {remainingTime} {t("min") || "min"}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              label={t("templateName") || "Template Name"}
              value={formData.tst_name}
              onChange={(e) => setFormData({ ...formData, tst_name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label={t("description") || "Description"}
              value={formData.tst_description}
              onChange={(e) => setFormData({ ...formData, tst_description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <TextField
              label={t("totalDuration") || "Total Duration (minutes)"}
              type="number"
              value={formData.tst_duration}
              onChange={(e) => setFormData({ ...formData, tst_duration: e.target.value })}
              fullWidth
              required
              inputProps={{ min: 1 }}
            />

            {/* Remaining Time Display */}
            {formData.tst_duration && (
              <Alert
                severity={remainingTime >= 0 ? "info" : "warning"}
                sx={{ borderRadius: 2 }}
              >
                <Typography variant="body2">
                  {t("remainingTime") || "Remaining Time"}:{" "}
                  <strong>{remainingTime >= 0 ? `${remainingTime} minutes` : `${Math.abs(remainingTime)} minutes over`}</strong>
                </Typography>
              </Alert>
            )}

            <Divider />

            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t("exercises") || "Exercises"}
            </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddItem}
                  sx={{ textTransform: "none" }}
                >
                  {t("addExercise") || "Add Exercise"}
                </Button>
              </Box>

              {formData.items.map((item, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {t("exercise")} {index + 1}
                      </Typography>
                      {item.tsti_duration && (
                        <Typography variant="caption" color={getRemainingSequenceTime(index) >= 0 ? "text.secondary" : "error.main"} sx={{ display: "block", mt: 0.5 }}>
                          {t("remainingTime") || "Remaining"}: {getRemainingSequenceTime(index)} {t("min") || "min"}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveItemUp(index)}
                        disabled={index === 0}
                        sx={{ color: "primary.main" }}
                        title={t("moveUp") || "Move Up"}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveItemDown(index)}
                        disabled={index === formData.items.length - 1}
                        sx={{ color: "primary.main" }}
                        title={t("moveDown") || "Move Down"}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveItem(index)}
                        sx={{ color: "error.main" }}
                        title={t("delete") || "Delete"}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>{t("exerciseType") || "Exercise Type"}</InputLabel>
                        <Select
                          value={item.type_id || ""}
                          onChange={(e) => handleItemChange(index, "type_id", e.target.value)}
                          label={t("exerciseType") || "Exercise Type"}
                          sx={{ 
                            "& .MuiSelect-select": {
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            },
                          }}
                          renderValue={(selected) => {
                            if (!selected) return "";
                            const selectedType = types.find(t => t.type_id === selected);
                            if (!selectedType) return "";
                            if (isPersonalizedExerciseType(selectedType)) {
                              return t("personalizedExercise", "Personalized exercise");
                            }
                            return localizedTypeName(selectedType, currentlng) || selectedType.type_name_en;
                          }}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                maxWidth: "90vw",
                              },
                            },
                          }}
                        >
                          {types.map((type) => (
                            <MenuItem key={type.type_id} value={type.type_id} sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                              {isPersonalizedExerciseType(type)
                                ? t("personalizedExercise", "Personalized exercise")
                                : localizedTypeName(type, currentlng) || type.type_name_en}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {isPersonalizedExerciseType(types.find((row) => row.type_id === item.type_id)) ? (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "templatePersonalizedSlotHint",
                            "Each player will run the exercise Pingflo assigned for their primary SMART objective, after you validate it. No shared catalogue drill is stored on this block."
                          )}
                        </Typography>
                      </Grid>
                    ) : (
                    <Grid item xs={12}>
                      <div className="tst-assign-mode" role="group" aria-label={t("templateAssignMode") || "Exercise assignment"}>
                        <button
                          type="button"
                          className={`tst-assign-mode__btn${!item.defer_exercise ? " is-active" : ""}`}
                          aria-pressed={!item.defer_exercise}
                          onClick={() => handleItemChange(index, "defer_exercise", false)}
                        >
                          {t("templateExerciseSet") || "Exercise set"}
                        </button>
                        <button
                          type="button"
                          className={`tst-assign-mode__btn${item.defer_exercise ? " is-active" : ""}`}
                          aria-pressed={!!item.defer_exercise}
                          onClick={() => {
                            const newItems = [...formData.items];
                            newItems[index] = {
                              ...newItems[index],
                              defer_exercise: true,
                              ex_id: "",
                              base_exercise: null,
                            };
                            setFormData({ ...formData, items: newItems });
                          }}
                        >
                          {t("templateTypeOnly") || "Type only"}
                        </button>
                      </div>
                      {item.defer_exercise ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {t(
                            "templateTypeOnlyHint",
                            "The session runs this exercise type. You choose the catalogue exercise during the session, or after it."
                          )}
                        </Typography>
                      ) : (
                        <CatalogExercisePicker
                          value={item.base_exercise || null}
                          onChange={(ex) => handleBaseExerciseChange(index, ex)}
                        />
                      )}
                    </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={4}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{t("duration") || "Duration"}</span>
                      <DurationMinutesSecondsInput
                        value={item.tsti_duration}
                        onChange={(next) => handleItemChange(index, "tsti_duration", next)}
                      />
                    </div>
                    </Grid>
                    {types.find(t => t.type_id === item.type_id)?.type_name_en === "Blocks of competency" && (
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={item.use_recommended_cb || false}
                              onChange={(e) => {
                                const newItems = [...formData.items];
                                newItems[index] = { ...newItems[index], use_recommended_cb: e.target.checked };
                                setFormData({ ...formData, items: newItems });
                              }}
                              color="primary"
                            />
                          }
                          label={t("useRecommendedCB") || "Use recommended competency block (CB will be chosen based on group level & goals when template is applied)"}
                        />
                        {item.use_recommended_cb && (
                          <FormControl size="small" sx={{ mt: 1, minWidth: 240 }}>
                            <InputLabel>{t("levelSlotRole") || "Slot role"}</InputLabel>
                            <Select
                              value={item.tsti_slot_role || ""}
                              label={t("levelSlotRole") || "Slot role"}
                              onChange={(e) => handleItemChange(index, "tsti_slot_role", e.target.value)}
                            >
                              <MenuItem value="">{t("levelSlotRoleAny") || "Any allowed block"}</MenuItem>
                              <MenuItem value="regularity">{t("levelSlotRole_regularity") || "Regularity"}</MenuItem>
                              <MenuItem value="game_scheme">{t("levelSlotRole_game_scheme") || "Game scheme"}</MenuItem>
                              <MenuItem value="personalized">{t("levelSlotRole_personalized") || "Personalized"}</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </Grid>
                    )}
                  </Grid>

                  {/* Sequences Section */}
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {t("sequences") || "Sequences"}
                      </Typography>
                    </Box>

                    {(!item.sequences || item.sequences.length === 0) && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {t("noSequencesAdded") || "No sequences added. Click 'Add Sequence' to define player roles and durations."}
                      </Alert>
                    )}

                    {item.sequences && item.sequences.map((sequence, seqIndex) => (
                      <Paper
                        key={seqIndex}
                        sx={{
                          p: 2,
                          mb: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          bgcolor: "background.paper",
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {sequence.sequence_order != null && sequence.sequence_order > 0 ? sequence.sequence_order : seqIndex + 1}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveSequenceUp(index, seqIndex)}
                              disabled={seqIndex === 0}
                              sx={{ color: "primary.main" }}
                              title={t("moveUp") || "Move Up"}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveSequenceDown(index, seqIndex)}
                              disabled={seqIndex === (item.sequences.length - 1)}
                              sx={{ color: "primary.main" }}
                              title={t("moveDown") || "Move Down"}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveSequence(index, seqIndex)}
                              sx={{ color: "error.main" }}
                              title={t("delete") || "Delete"}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {!item.use_recommended_cb && (() => {
                            const currentSubTypes = subTypesByType[item.type_id] || subTypes.filter((st) => st.type_id === item.type_id);
                            const hasSubtypes = currentSubTypes.length > 0;
                            if (!hasSubtypes) return null;
                            return (
                              <FormControl fullWidth required>
                                <InputLabel>{t("exerciseSubType") || "Sub-Type"}</InputLabel>
                                <Select
                                  value={sequence.sub_type_id || item.sub_type_id || ""}
                                  onChange={(e) => handleSequenceChange(index, seqIndex, "sub_type_id", e.target.value)}
                                  label={t("exerciseSubType") || "Sub-Type"}
                                  disabled={!item.type_id}
                                  sx={{ 
                                    "& .MuiSelect-select": {
                                      whiteSpace: "normal",
                                      wordBreak: "break-word",
                                    },
                                  }}
                                  renderValue={(selected) => {
                                    if (!selected) return "";
                                    const selectedSubType = currentSubTypes.find(st => st.sub_type_id === selected);
                                    return selectedSubType ? (localizedSubTypeName(selectedSubType, currentlng) || selectedSubType.sub_type_name_en) : "";
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      style: {
                                        maxHeight: 300,
                                        maxWidth: "90vw",
                                      },
                                    },
                                  }}
                                >
                                  {currentSubTypes.map((subType) => (
                                    <MenuItem key={subType.sub_type_id} value={subType.sub_type_id} sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                                      {localizedSubTypeName(subType, currentlng) || subType.sub_type_name_en}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            );
                          })()}

                          <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: "block", color: "text.secondary" }}>
                                {t("playerA") || "Player A"}
                              </Typography>
                              <FormControl fullWidth size="small">
                                <InputLabel>{t("role") || "Role"}</InputLabel>
                                <Select
                                  value={sequence.player_a_role === "both" ? "active" : (sequence.player_a_role || "active")}
                                  onChange={(e) => handleSequenceChange(index, seqIndex, "player_a_role", e.target.value)}
                                  label={t("role") || "Role"}
                                >
                                  <MenuItem value="active">{t("performance_role", "Performance")}</MenuItem>
                                  <MenuItem value="sparring">{t("sparring") || "Sparring"}</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>

                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: "block", color: "text.secondary" }}>
                                {t("playerB") || "Player B"}
                              </Typography>
                              <FormControl fullWidth size="small">
                                <InputLabel>{t("role") || "Role"}</InputLabel>
                                <Select
                                  value={sequence.player_b_role === "both" ? "sparring" : (sequence.player_b_role || "sparring")}
                                  onChange={(e) => handleSequenceChange(index, seqIndex, "player_b_role", e.target.value)}
                                  label={t("role") || "Role"}
                                >
                                  <MenuItem value="active">{t("performance_role", "Performance")}</MenuItem>
                                  <MenuItem value="sparring">{t("sparring") || "Sparring"}</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                          </Box>

                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{t("sequenceDuration") || "Sequence duration"}</span>
                          <DurationMinutesSecondsInput
                            value={sequence.sequence_duration || ""}
                            onChange={(next) =>
                              handleSequenceChange(
                                index,
                                seqIndex,
                                "sequence_duration",
                                next
                              )
                            }
                          />
                        </div>
                        </Box>
                        {getRemainingSequenceTime(index) < 0 && (
                          <Alert severity="error" sx={{ mt: 2 }}>
                            {t("sequencesDurationExceedsExercise") || "Total sequences duration exceeds exercise duration"}
                          </Alert>
                        )}
                      </Paper>
                    ))}
                    
                    {/* Add Sequence Button at the end */}
                    {item.sequences && item.sequences.length > 0 && (
                      <Box sx={{ mt: 1, mb: 2 }}>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleAddSequence(index)}
                          variant="outlined"
                          fullWidth
                        >
                          {t("addSequence") || "Add Sequence"}
                        </Button>
                      </Box>
                    )}
                    
                    {/* Add Sequence Button when no sequences */}
                    {(!item.sequences || item.sequences.length === 0) && (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddSequence(index)}
                        variant="outlined"
                        fullWidth
                        sx={{ mb: 2 }}
                      >
                        {t("addSequence") || "Add Sequence"}
                      </Button>
                    )}
                  </Box>
                  
                  {/* Add Exercise Button at the end of each exercise */}
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddItem}
                      variant="outlined"
                      fullWidth
                      sx={{ textTransform: "none" }}
                    >
                      {t("addExercise") || "Add Exercise"}
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: "none" }}>
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ textTransform: "none" }}
            disabled={remainingTime < 0}
          >
            {t("save") || "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog - works on mobile and PC */}
      <Dialog
        open={Boolean(templateToDelete)}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-template-dialog-title"
        aria-describedby="delete-template-dialog-description"
        PaperProps={{ sx: { borderRadius: 2, minWidth: 280 } }}
      >
        <DialogTitle id="delete-template-dialog-title">
          {t("deleteTemplate") || "Delete template"}
        </DialogTitle>
        <DialogContent>
          <Typography id="delete-template-dialog-description">
            {t("confirmDeleteTemplate", "Are you sure you want to delete this template?")}
          </Typography>
          {templateToDelete?.tst_name && (
            <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
              &quot;{templateToDelete.tst_name}&quot;
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleting} color="inherit">
            {t("cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            autoFocus
          >
            {deleting ? (t("deleting") || "Deleting…") : (t("delete") || "Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </div>
    </AdminLayout>
  );
}

